<template>
  <v-footer
    fixed
    app
    inset
    :height="theme.footer_height"
    :color="theme.footer_bg_color"
  >
    <template v-if="isMember">
      <v-btn
        v-if="allowCheckout.all"
        block
        dark
        large
        class="btn"
        :color="theme.btn_color_secondary"
        @click="confirm"
      >
        <v-layout row>
          <v-flex
            xs12
            class="text-xs-center nowrap-text"
          >
            {{ $t('member_submit') }}
          </v-flex>
        </v-layout>
      </v-btn>
    </template>
    <template v-else>
      <v-btn
        v-if="allowLogin"
        block
        dark
        large
        class="btn pl-0 pr-0 text-xs-center"
        :color="theme.btn_color_secondary"
        @click="register"
      >
        <v-layout row>
          <v-flex
            xs12
            class="text-xs-center nowrap-text"
          >
            {{ registerBtn }}
          </v-flex>
        </v-layout>
      </v-btn>
      <v-btn
        v-if="allowCheckout.guest"
        block
        dark
        large
        class="btn pl-0 pr-0 text-xs-center"
        :color="theme.btn_color_secondary"
        @click="confirm"
      >
        <v-layout row>
          <v-flex
            xs12
            class="text-xs-center nowrap-text"
          >
            {{ $t('guest_submit') }}
          </v-flex>
        </v-layout>
      </v-btn>
    </template>
  </v-footer>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  data: () => ({
    hash: null
  }),
  computed: {
    ...mapGetters('config', [
      'theme', 'redirectUrl', 'paymentTypes', 'mode', 'restaurants', 'posType',
      'allowCheckout', 'allowLogin', 'enableMemberPoints', 'withQueueNo', 'posType'
    ]),
    ...mapGetters('member', [
      'profile', 'isMember'
    ]),
    ...mapGetters('auth', [
      'user', 'authenticated'
    ]),
    ...mapGetters('menu', [
      'catListIds'
    ]),
    ...mapGetters('order', [
      'activeOrder', 'orderMsg', 'activeOrderPayments', 'orders'
    ]),
    ...mapGetters('cart', [
      'cart', 'cartQty', 'pickupDate', 'pickupTime', 'cartMsg', 'times', 'guestDetailsStatus'
    ]),
    registerBtn () {
      if (this.enableMemberPoints) {
        return this.$t('signup_earn_points')
      }
      return this.$t('signup')
    }
  },
  methods: {
    async logPaymentData () {
      try {
        let content = this.activeOrder
        if (this.isMember) {
          content.member = this.profile
        }
        await axios.post('/api/log', {
          action: 'confirmOrder',
          content: content
        })
      } catch (e) { }
    },
    register () {
      if (this.authenticated) {
        this.$router.push({ name: 'register2' })
      } else {
        this.$router.push({ name: 'register' })
      }
    },
    async confirm () {
      if (this.rInfoExpiredAlert()) {
        return
      } else if (this.noTableOrAddressAlert()) {
        return
      }

      if (this.shouldExpireSession()) {
        await this.$store.dispatch('responseMessage', {
          type: 'error',
          text: 'Session Expired',
          noBtn: this.$mq === 'sm' || this.$mq === 'md' || this.$mq === 'lg' || this.$mq === 'xl'
        })
        await this.$store.dispatch('resetAppRelatedData')
        await this.$store.dispatch('config/resetModeSelected')
        if (this.isMember) {
          await this.$store.dispatch('fetchMembershipInfo')
        }
        await this.$router.push({ name: 'welcome' })
      }
      // This function might takes long to load, so add a loading indicator
      this.$bus.$emit('showFullLoading', true)

      // Check pickup date and time
      if (this.mode.isTakeaway || this.mode.isDelivery) {
        if (!this.isMember && this.guestDetailsStatus.isEmpty) {
          this.$bus.$emit('showFullLoading', false)
          this.$router.push({ name: 'guest_details' })
          return
        }
        await this.$store.dispatch('cart/fetchTakeawayTimes', this.pickupDate)
        const time = this.times.find(t => t.time === this.pickupTime && t.availTime > 0)
        if (!time) {
          this.$bus.$emit('showFullLoading', false)
          this.$store.dispatch('cart/resetPickupDateTime')
          this.$store.dispatch('responseMessage', {
            type: 'error',
            title: this.$t('invalid_time'),
            text: this.$t('reselect_time'),
            modal: true,
            buttons: [{ text: 'Ok', path: 'cart' }]
          })
          return
        }
      }

      // Check availability schema
      await this.$store.dispatch('menu/fetchMenuCats')
      const notAvailableItems = []
      this.cart.forEach(item => {
        if (!item.is_promo && !this.catListIds.includes(item.category_id) && !item.member_voucher_id) {
          notAvailableItems.push(item.name)
        }
      })
      if (notAvailableItems.length > 0) {
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: this.$t('item_unavailable'),
          text: 'The following item(s) is unavailable:' + notAvailableItems.join(', '),
          modal: true
        })
        this.$bus.$emit('showFullLoading', false)
        return
      }

      await this.$store.dispatch('cart/checkCartItems')
      if (this.cartMsg) {
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Unable to submit',
          text: this.cartMsg,
          modal: true
        })
        this.$bus.$emit('showFullLoading', false)
        return
      }

      await this.$store.dispatch('cart/checkCartDiscountInfo')
      if (this.cartMsg) {
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Unable to submit',
          text: this.cartMsg,
          modal: true
        })
        this.$bus.$emit('showFullLoading', false)
        return
      }

      if (this.posType === 0) {
        await this.$store.dispatch('order/checkFinalOrder', this.activeOrderPayments)
      } else {
        await this.$store.dispatch('order/checkFinalOrder2', this.activeOrderPayments)
      }

      if (this.orderMsg) {
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Unable to submit',
          text: this.orderMsg,
          modal: true
        })
        this.$bus.$emit('showFullLoading', false)
        return
      }

      if (this.posType === 0) {
        await this.$store.dispatch('order/confirmActiveOrder')
      } else {
        await this.$store.dispatch('order/confirmActiveOrder2')
      }

      if (this.orderMsg) {
        this.$store.dispatch('responseMessage', {
          type: 'error',
          title: 'Unable to submit',
          text: this.orderMsg,
          modal: true
        })
        this.$bus.$emit('showFullLoading', false)
        return
      }

      await this.$store.dispatch('order/addOrder')
      const id = this.activeOrder.id

      await this.$store.dispatch('order/fetchActiveQueue')
      this.$store.dispatch('changeTable', '')
      // Only reset if there are multiple outlets
      // if (this.restaurants && this.restaurants.length > 1) {
      //   this.$store.dispatch('config/changeRid', 0)
      // }
      this.$store.dispatch('cart/resetCart')
      this.$store.dispatch('cart/resetPickupTime')
      this.$store.dispatch('order/resetPaymentMode')
      this.$store.dispatch('order/resetOrder')
      this.$bus.$emit('showFullLoading', false)

      if (!this.withQueueNo && this.orders.find(o => parseInt(o.id) === parseInt(id))) {
        this.$router.push({ name: 'order', params: { id: id.toString() }})
      } else {
        this.$router.push({ name: 'orders' })
      }
    },
  },
}
</script>

<style scoped>
.btn {
  margin: 10px;
}
.nowrap-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/ConfirmFooter.vue